import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  Inject,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { GoogleMap } from '@angular/google-maps';
import { MAP_CONFIG, MapConfig } from '@x/common/map/map';
import { PointInput } from '@x/schemas/ecommerce';
import { BehaviorSubject } from 'rxjs';

@Component({
  selector: 'x-address-map',
  templateUrl: './address-map.component.html',
  styleUrls: ['./address-map.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddressMapComponent implements AfterViewInit {
  mapLoading$ = new BehaviorSubject<boolean>(true);
  mapOptions: google.maps.MapOptions;

  //Inputs
  @Input() showLoading = true;
  @HostBinding('style.height')
  @Input()
  mapHeight: string = '300px';
  @Input() showCoordinates: boolean = false;
  @Input() defaultZoom: number = 10;
  @Input() defaultCoordinates: PointInput | null = { lat: -33.822505, lng: 18.929985 };
  @Input() showBounds: boolean = false;
  @Input() dragMarker: boolean = true;

  @Output() coordinatesUpdate = new EventEmitter<PointInput>();

  _markers: google.maps.MarkerOptions[] = [];
  @Input() set markers(markers: google.maps.MarkerOptions[]) {
    if (!markers) return;

    this._markers = Array.isArray(markers) ? markers : [markers];
    this.centerMapOnAllFeatures();
  }

  _polygons: google.maps.PolygonOptions[] = [];
  @Input() set polygons(polygons: google.maps.PolygonOptions[]) {
    if (!polygons) return;

    this._polygons = Array.isArray(polygons) ? polygons : [polygons];
    this.centerMapOnAllFeatures();
  }

  _addressMarker: PointInput;
  @Input() set addressMarker(marker: PointInput | undefined | null) {
    if (!marker) return;
    this._addressMarker = marker;
    this.centerMapOnAllFeatures();
  }

  @ViewChild('mapControl', { static: false })
  mapControl: GoogleMap;

  constructor(
    @Inject(MAP_CONFIG)
    public mapThemeConfig: MapConfig,
    private cd: ChangeDetectorRef,
  ) {}

  centerMapOnAllFeatures() {
    if (!this.mapControl) return;

    //fitbounds
    if (this.showBounds && (this._markers.length || this._polygons.length)) {
      let bounds = new google.maps.LatLngBounds();

      if (this._addressMarker) bounds.extend(this._addressMarker);

      this._markers.forEach((m) => {
        if (m.position) bounds.extend(m.position);
      });

      this._polygons.forEach((poly) => {
        poly.paths?.forEach((path) => {
          Array.isArray(path) ? path.forEach((p) => bounds.extend(p)) : bounds.extend(path);
        });
      });

      this.mapControl.fitBounds(bounds, 20);
      return;
    }

    //panMap
    if (this._addressMarker) this.mapControl.panTo(this._addressMarker);
  }

  markerDragEnd(e: google.maps.MapMouseEvent) {
    const { latLng } = e;
    if (!latLng) return;

    const coordinates: PointInput = {
      lat: latLng.lat(),
      lng: latLng.lng(),
    };

    //update coordinates
    this.coordinatesUpdate.emit(coordinates);
    this.centerMapOnAllFeatures();
  }

  mapInit() {
    // use setTimeout: mapControl not ready
    setTimeout(() => {
      this.centerMapOnAllFeatures();
    }, 0);
  }

  ngAfterViewInit(): void {
    this.configureMap();
  }

  private configureMap() {
    this.mapOptions = {
      gestureHandling: 'greedy',
      zoom: this.defaultZoom,
      mapTypeId: 'roadmap',
      backgroundColor: 'transparent',
      disableDefaultUI: true,
      zoomControl: false,
      scaleControl: false,
      mapTypeControl: false,
      mapTypeControlOptions: {
        style: google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
        position: google.maps.ControlPosition.TOP_RIGHT,
      },
      center: this.defaultCoordinates,
      styles: this.mapThemeConfig.theme,
    };
    this.mapLoading$.next(false);
    this.cd.detectChanges();
  }
}
